import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ExternalLink, ArrowUpRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import FeatureSpotlight, { useFeatureSpotlight } from './FeatureSpotlight';

/**
 * InstanceSwitcher — entrar al helpdesk de un tenant desde el nuestro, ya logueado.
 *
 * Wolfsohn corre su propia instancia (otro stack, otra base, su marca). Nuestros agentes
 * tienen usuario allá pero con clave aparte, y una credencial que hay que buscar es una
 * credencial que no se usa: al día siguiente de la migración el colegio la usaba a diario
 * y de los nuestros no había entrado nadie.
 *
 * El botón pide un pase de 60 segundos al backend y abre la otra instancia en una pestaña
 * nueva. No se comparte sesión: el destino emite la suya contra SU tabla de usuarios, así
 * que el acceso se sigue administrando allá.
 *
 * Se auto-oculta: si el puente no está configurado, /targets devuelve [] y no se renderiza.
 * La primera vez explica qué es, porque un botón con el nombre de un cliente no se entiende solo.
 */
export default function InstanceSwitcher() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [targets, setTargets] = useState([]);
  const [abriendo, setAbriendo] = useState(null);
  const [verAyuda, setVerAyuda] = useState(false);
  const anchorRef = useRef(null);
  const { shouldShow, dismiss } = useFeatureSpotlight('instance_bridge_v1');

  const esInterno = user && user.role !== 'client';

  useEffect(() => {
    if (!esInterno) return;
    api.get('/instance-bridge/targets')
      .then(({ data }) => setTargets(data?.targets || []))
      .catch(() => setTargets([]));
  }, [esInterno]);

  const entrar = useCallback(async (destino) => {
    setAbriendo(destino.key);
    // La pestaña se abre ANTES del await: si se abre después, el navegador la trata como
    // popup no solicitado y la bloquea.
    const pestania = window.open('', '_blank');
    try {
      const { data } = await api.post(`/instance-bridge/${destino.key}/launch`);
      if (!data?.url) throw new Error('sin url');
      if (pestania) pestania.location = data.url;
      else window.location.href = data.url;
    } catch {
      if (pestania) pestania.close();
      toast.error(t('instanceBridge.error'));
    } finally {
      setAbriendo(null);
    }
  }, [t]);

  if (!esInterno || targets.length === 0) return null;

  const ayuda = verAyuda ? ReactDOM.createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) setVerAyuda(false); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        background: 'rgba(0,0,0,0.5)', padding: 16,
      }}
    >
      <div
        onMouseDown={(e) => e.stopPropagation()}
        className="card"
        style={{ margin: 0, maxWidth: 520, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}
      >
        <div className="card-header">
          <div className="card-title">
            <ExternalLink size={18} /> {t('instanceBridge.helpTitle')}
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={() => setVerAyuda(false)} aria-label={t('common.close')}>
            <X size={16} />
          </button>
        </div>
        <p style={{ color: 'var(--c-text)', lineHeight: 1.6 }}>{t('instanceBridge.helpBody1')}</p>
        <p style={{ color: 'var(--c-text-muted)', lineHeight: 1.6 }}>{t('instanceBridge.helpBody2')}</p>
        <p style={{ color: 'var(--c-text-muted)', lineHeight: 1.6 }}>{t('instanceBridge.helpBody3')}</p>
      </div>
    </div>, document.body) : null;

  return (
    <>
      <div ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
        {targets.map((d) => (
          <button
            key={d.key}
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => entrar(d)}
            disabled={abriendo === d.key}
            title={t('instanceBridge.openTitle', { name: d.name })}
          >
            <ExternalLink size={14} />
            {d.name}
            <ArrowUpRight size={12} />
          </button>
        ))}
      </div>
      <FeatureSpotlight
        anchorRef={anchorRef}
        show={shouldShow}
        onDismiss={() => { dismiss(); setVerAyuda(true); }}
        title={t('instanceBridge.spotlightTitle')}
        body={t('instanceBridge.spotlightBody')}
        placement="bottom"
      />
      {ayuda}
    </>
  );
}
